/** Hover quick add for collection product cards */
(function () {
  const CARD = 'card-product.card-product--hover-quick-add';
  const DESKTOP_MQ = window.matchMedia('(min-width: 991px)');
  const swatchCache = new Map();
  const swatchProducts = new WeakMap();
  const preloadedImages = new Set();

  const cards = () => document.querySelectorAll(CARD);
  const getQuickAdd = (card) => card.querySelector(':scope > .card > .card__content .quick-add--hover');

  function getCustomField(sku, key) {
    return sku.customFields?.find((f) => f.key === key)?.value ?? '';
  }

  function getSizeFieldKey(skus, product) {
    const sizeValues = new Set();
    const colorValues = new Set();
    const colorName = (product?.colorName || '').toLowerCase();

    (skus || []).forEach((sku) => {
      const size = getCustomField(sku, 'size');
      const color = getCustomField(sku, 'color');
      if (size && size.toLowerCase() !== colorName) sizeValues.add(size);
      if (color) colorValues.add(color);
    });

    return sizeValues.size >= colorValues.size && sizeValues.size > 0 ? 'size' : 'color';
  }

  function skusToVariants(skus, product) {
    const sizeKey = getSizeFieldKey(skus, product);
    const colorKey = sizeKey === 'size' ? 'color' : 'size';

    return (skus || []).map((sku) => ({
      id: sku.id,
      available: sku.availability === 'InStock',
      option1: getCustomField(sku, colorKey) || product?.colorName || '',
      option2: getCustomField(sku, sizeKey) || getCustomField(sku, colorKey),
      option3: null,
    }));
  }

  function fetchSwatchProducts(url) {
    if (swatchCache.has(url)) return Promise.resolve(swatchCache.get(url));
    return fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const products = JSON.parse(
          new DOMParser().parseFromString(html, 'text/html').querySelector('[js-products-json]')?.innerHTML || '[]'
        );
        swatchCache.set(url, products);
        return products;
      });
  }

  function getImage533Url(imageUrl) {
    if (!imageUrl) return '';
    const baseUrl = imageUrl.split('?')[0].replace(/_(master|\d+x\d+|\d+x)\.(jpg|jpeg|png|webp|gif)$/i, '.$2');
    const extension = baseUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[1] || 'jpg';
    const queryString = imageUrl.includes('?') ? `?${imageUrl.split('?')[1]}` : '';
    return `${baseUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '')}_533x.${extension}${queryString}`;
  }

  function preloadImage(imageUrl) {
    const src = getImage533Url(imageUrl);
    if (!src || preloadedImages.has(src)) return;
    preloadedImages.add(src);
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }

  function preloadProductImages(product) {
    preloadImage(product.imageUrl);
    preloadImage(product.alternateImageUrls?.[0]);
  }

  function setImageSrcFast(img, imageUrl) {
    if (!img || !imageUrl) return;
    img.src = getImage533Url(imageUrl);
    img.removeAttribute('srcset');
  }

  function setQuickAddHeight(card) {
    const quickAdd = getQuickAdd(card);
    if (!quickAdd) return;

    const height = Math.max(
      quickAdd.offsetHeight,
      quickAdd.querySelector('.variant-options')?.scrollHeight || 0
    );

    const value = `${height}px`;
    if (card.style.getPropertyValue('--quick-add-height') === value) return;
    card.style.setProperty('--quick-add-height', value);
  }

  function updateSubmitButton(card, submitBtn, variant) {
    if (!submitBtn) return;

    const addText = card.dataset.addText || 'Add to cart';
    const soldOutText = card.dataset.soldOutText || 'Sold out';
    const span = submitBtn.querySelector('span');

    if (variant) {
      submitBtn.disabled = !variant.available;
      submitBtn.setAttribute('aria-disabled', String(!variant.available));
      if (span) span.textContent = variant.available ? addText : soldOutText;
      return;
    }

    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-disabled', 'true');
    if (span) span.textContent = addText;
  }

  function buildSizeOptionsHtml(product, variants, form, sectionId, productId) {
    const sizeKey = getSizeFieldKey(product.skus, product);
    const colorKey = sizeKey === 'size' ? 'color' : 'size';
    const colorName = (product.colorName || '').toLowerCase();
    const seen = new Set();

    return product.skus
      .reduce((sizes, sku) => {
        const val = getCustomField(sku, sizeKey) || getCustomField(sku, colorKey);
        if (!val || seen.has(val) || val.toLowerCase() === colorName) return sizes;
        seen.add(val);
        sizes.push({ size: val, sku });
        return sizes;
      }, [])
      .map(({ size, sku }) => {
        const available = variants.find((v) => String(v.id) === String(sku.id))?.available ?? false;
        const id = `ProductSelect-option-${sectionId}-${productId}-size-${String(size).replace(/\s/g, '-')}`;
        const disabled = available ? '' : ' disabled';
        const disableClass = available ? '' : ' js-variant-disable';

        return `
          <input type="radio" data-index="option2" name="size-${productId}" id="${id}" class="single-option-selector-${sectionId} single-option-selector__radio card-option-input${disableClass}" value="${String(size).replace(/"/g, '&quot;')}" form="${form.id}" data-option-value-id="${sku.id}" data-available="${available}"${disabled}>
          <label for="${id}" class="card-quick-add__size"${disabled ? ' aria-disabled="true"' : ''}>${size}</label>
        `;
      })
      .join('');
  }

  function buildPriceHtml(product) {
    const hasCompare = product.listPrice && parseFloat(product.listPrice) > parseFloat(product.price);
    return hasCompare && product.listPriceFormatted
      ? `<span class="price price--compare">${product.listPriceFormatted}</span> <span class="price price--sale">${product.priceFormatted || product.price}</span>`
      : `<span class="price">${product.priceFormatted || product.price}</span>`;
  }

  function getCardRefs(card) {
    if (card.__cardRefs) return card.__cardRefs;

    const form = card.querySelector('.card-product-form');
    card.__cardRefs = {
      form,
      mainImage: card.querySelector('.card__media .media img:first-of-type'),
      hoverImg: card.querySelector('.card__media .media img:nth-of-type(2)'),
      mediaLink: card.querySelector('.card__media-link'),
      titleLink: card.querySelector('.card__heading a'),
      priceEl: card.querySelector('.js-card-price'),
      variantsScript: card.querySelector('.js-card-variants-script'),
      sizeFieldset: card.querySelector('.js-card-size-fieldset'),
      variantInput: form?.querySelector('input[name="id"]'),
      submitBtn: form?.querySelector('button[type="submit"]'),
    };

    return card.__cardRefs;
  }

  function prepareSwatchProducts(card, products) {
    const refs = getCardRefs(card);
    if (!refs.form) return products.map((product) => ({ product, variants: skusToVariants(product.skus, product) }));

    const sectionId = card.dataset.sectionId || '';
    return products.map((product) => {
      const productId = String(product.productId);
      const variants = skusToVariants(product.skus, product);
      return {
        product,
        variants,
        sizeHtml: buildSizeOptionsHtml(product, variants, refs.form, sectionId, productId),
        priceHtml: buildPriceHtml(product),
      };
    });
  }

  function swapSwatchCurrent(container, clickedBtn) {
    const current = container.querySelector('.product__swatch-link--current');
    if (current === clickedBtn) return;

    current?.classList.remove('product__swatch-link--current');
    current?.removeAttribute('aria-current');

    clickedBtn.classList.add('product__swatch-link--current');
    clickedBtn.setAttribute('aria-current', 'true');
  }

  function switchProduct(card, prepared, state) {
    const { product, variants, sizeHtml, priceHtml } = prepared;
    const refs = getCardRefs(card);
    if (!refs.form || !refs.variantInput || !state) return;

    if (refs.mainImage && product.imageUrl) {
      setImageSrcFast(refs.mainImage, product.imageUrl);
      refs.mainImage.alt = product.name || '';
    }

    if (refs.hoverImg) {
      const hoverUrl = product.alternateImageUrls?.[0] || product.imageUrl;
      if (hoverUrl) setImageSrcFast(refs.hoverImg, hoverUrl);
    }

    if (product.url && refs.mediaLink) refs.mediaLink.href = product.url;

    if (refs.titleLink && product.url) {
      refs.titleLink.href = product.url;
      refs.titleLink.textContent = product.name || '';
    }

    if (refs.priceEl && refs.priceEl.innerHTML !== priceHtml) {
      refs.priceEl.innerHTML = priceHtml;
    }

    card.dataset.productId = String(product.productId);
    card.dataset.handle = product.handle;
    state.setVariants(variants);

    if (refs.variantsScript) refs.variantsScript.textContent = JSON.stringify(variants);

    if (refs.sizeFieldset && sizeHtml !== refs.sizeFieldset.innerHTML) {
      refs.sizeFieldset.innerHTML = sizeHtml;
    }

    refs.variantInput.value = '';
    refs.variantInput.disabled = true;
    updateSubmitButton(card, refs.submitBtn, null);
    state.updateVariant();

    requestAnimationFrame(() => setQuickAddHeight(card));
  }

  function renderSwatchMarkup(products, handle) {
    return products
      .map((product, index) => {
        const colorImg = product.colorImg || 'no-image';
        const colorHex = product.colorHex || 'transparent';
        const bgColor = colorImg.includes('no-image') ? colorHex : '';
        const bgImage = colorImg.includes('no-image') ? '' : `url(${colorImg})`;
        const name = product.colorName || product.name || '';
        const safeName = name.replace(/"/g, '&quot;');
        const style = `background-color:${bgColor};background-image:${bgImage};background-size:cover;`;
        const isCurrent = handle === product.handle;
        const currentClass = isCurrent ? ' product__swatch-link--current' : '';
        const currentAttr = isCurrent ? ' aria-current="true"' : '';

        return `<button type="button" class="product__swatch-link${currentClass}" aria-label="Select color ${safeName}" data-product-index="${index}" style="${style}" js-swatch-link${currentAttr}></button>`;
      })
      .join('');
  }

  function loadSwatchContainer(container) {
    const { tag, handle } = container.dataset;
    if (!tag || container.dataset.loaded !== 'false') return;

    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}collections/all/${encodeURIComponent(tag)}?view=json`;

    container.setAttribute('aria-busy', 'true');
    fetchSwatchProducts(url)
      .then((products) => {
        container.dataset.loaded = 'true';
        container.removeAttribute('aria-busy');
        container.innerHTML = renderSwatchMarkup(products, handle);
        swatchProducts.set(container, products);
        container.classList.add('is-loaded');

        products.forEach(preloadProductImages);

        const card = container.closest(CARD);
        if (!card) return;

        const prepared = prepareSwatchProducts(card, products).map((entry, index) => ({
          ...entry,
          index,
        }));
        card.__preparedSwatches = prepared;

        requestAnimationFrame(() => setQuickAddHeight(card));
      })
      .catch(() => {
        container.dataset.loaded = 'true';
        container.removeAttribute('aria-busy');
      });
  }

  function initSwatchLinks() {
    const containers = document.querySelectorAll(`${CARD} [js-swatch-links-wrapper][data-loaded="false"]`);
    if (!containers.length) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        loadSwatchContainer(entry.target);
      });
    }, { rootMargin: '200px', threshold: 0.01 });

    containers.forEach((container) => observer.observe(container));
  }

  function initVariantForm(form) {
    const card = form.closest(CARD);
    if (!card || card.dataset.variantJsInit) return;

    const productId = card.dataset.productId;
    const script = card.querySelector('.js-card-variants-script') || document.querySelector(`[data-product-variants-${productId}]`);
    if (!script) return;

    card.dataset.variantJsInit = 'true';

    let variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch {
      card.dataset.variantJsInit = '';
      return;
    }

    const refs = getCardRefs(card);

    function getSelectedOptions() {
      const selected = {};
      form.querySelectorAll('.single-option-radio input:checked').forEach((input) => {
        selected[input.dataset.index] = input.value;
      });
      return selected;
    }

    function findVariant() {
      const selected = getSelectedOptions();
      if (refs.sizeFieldset && !refs.sizeFieldset.querySelector('input:checked:not(:disabled)')) return null;

      return variants.find(
        (variant) =>
          (!selected.option1 || variant.option1 === selected.option1) &&
          (!selected.option2 || variant.option2 === selected.option2) &&
          (!selected.option3 || variant.option3 === selected.option3)
      );
    }

    function updateVariant() {
      const variant = findVariant();
      if (variant) {
        refs.variantInput.value = variant.id;
        refs.variantInput.disabled = false;
      } else {
        refs.variantInput.value = '';
        refs.variantInput.disabled = true;
      }
      updateSubmitButton(card, refs.submitBtn, variant);
    }

    card.__variantState = {
      setVariants: (nextVariants) => {
        variants = nextVariants;
      },
      updateVariant,
    };

    form.addEventListener('change', (event) => {
      if (event.target.matches('.card-option-input')) updateVariant();
    });

    refs.sizeFieldset?.addEventListener('click', (event) => {
      const label = event.target.closest('label[for]');
      if (!label) return;
      const radio = document.getElementById(label.getAttribute('for'));
      if (radio && !radio.disabled) radio.checked = true;
      updateVariant();
    });

    updateVariant();
  }

  function initVariantForms() {
    document.querySelectorAll(`${CARD} .card-product-form`).forEach(initVariantForm);
  }

  function initQuickAddHeights() {
    if (!DESKTOP_MQ.matches) return;

    cards().forEach((card) => {
      setQuickAddHeight(card);
      if (card.dataset.quickAddHeightObserved) return;

      const quickAdd = getQuickAdd(card);
      if (!quickAdd || typeof ResizeObserver === 'undefined') return;

      card.dataset.quickAddHeightObserved = 'true';
      new ResizeObserver(() => requestAnimationFrame(() => setQuickAddHeight(card))).observe(quickAdd);
    });
  }

  function resetCards() {
    cards().forEach((card) => {
      delete card.dataset.variantJsInit;
      delete card.dataset.quickAddHeightObserved;
      delete card.__cardRefs;
      delete card.__variantState;
      delete card.__preparedSwatches;
    });
  }

  function init() {
    if (!document.querySelector(CARD)) return;
    initVariantForms();
    initSwatchLinks();
    initQuickAddHeights();
  }

  function handleSwatchClick(event) {
    const btn = event.target.closest(`${CARD} [js-swatch-link]`);
    if (!btn || btn.classList.contains('product__swatch-link--current')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const container = btn.closest('[js-swatch-links-wrapper]');
    const card = btn.closest(CARD);
    if (!card || !container) return;

    swapSwatchCurrent(container, btn);

    if (!card.__variantState) initVariantForm(card.querySelector('.card-product-form'));

    const index = parseInt(btn.dataset.productIndex, 10);
    let prepared = card.__preparedSwatches?.[index];

    if (!prepared) {
      const products = swatchProducts.get(container) || [];
      prepared = prepareSwatchProducts(card, products)[index];
      if (prepared) prepared = { ...prepared, index };
    }

    if (card.__variantState && prepared) {
      switchProduct(card, prepared, card.__variantState);
    }
  }

  function prefetchSwatchesOnHover(event) {
    const container = event.target.closest(`${CARD} [js-swatch-links-wrapper][data-loaded="false"]`);
    if (container) {
      loadSwatchContainer(container);
      return;
    }

    const btn = event.target.closest(`${CARD} [js-swatch-link]:not(.product__swatch-link--current)`);
    if (!btn) return;

    const card = btn.closest(CARD);
    const index = parseInt(btn.dataset.productIndex, 10);
    const product = card?.__preparedSwatches?.[index]?.product
      || swatchProducts.get(btn.closest('[js-swatch-links-wrapper]'))?.[index];

    if (product) preloadProductImages(product);
  }

  document.addEventListener('mouseover', prefetchSwatchesOnHover, { passive: true });

  document.addEventListener('click', handleSwatchClick, true);
  document.addEventListener('mousedown', (event) => {
    if (event.target.closest(`${CARD} [js-swatch-link]:not(.product__swatch-link--current)`)) {
      event.preventDefault();
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    resetCards();
    init();
  });

  document.addEventListener('product-grid:updated', () => {
    resetCards();
    init();
  });

  DESKTOP_MQ.addEventListener('change', () => {
    resetCards();
    init();
  });
})();
